import { useCallback, useState } from "react";

import { styled } from "@linaria/react";
import { match, P } from "ts-pattern";

import FlexWrapper, {
  AlignItems,
  FlexDirection,
  JustifyContent,
} from "@gx/dls/containers/FlexWrapper";
import Text, { TextSize, TextVariant } from "@gx/dls/text/Text";
import { withTheme, type PropsWithTheme } from "@gx/dls/theme";
import { ProjectMappingMethod } from "@gx/ts-contracts/services/project/resources/project_configuration/project_configuration_pb";

import {
  MAPPING_METHOD_TO_DESCRIPTION_MAP,
  MAPPING_METHOD_TO_ICON_MAP,
  MAPPING_METHOD_TO_IS_RECOMMENDED_MAP,
  MAPPING_METHOD_TO_LABEL_MAP,
} from "@/pages/projects/forms/create/components/steps/select-mapping-method/components/constants";
import {
  MappingMethodCardAutoIntrospectGraphic,
  MappingMethodCardBlankGraphic,
  MappingMethodCardJsonGraphic,
} from "@/pages/projects/forms/create/components/steps/select-mapping-method/components/MappingMethodCardGraphic";

interface MappingMethodCardProps {
  method: ProjectMappingMethod;
  isSelected: boolean;
  onSelect: () => void;
}

const MappingMethodCardWrapper = withTheme(styled.div<
  PropsWithTheme<{ $isSelected: boolean; $isRecommended: boolean }>
>`
  display: flex;
  width: 100%;
  height: 100%;
  padding: 16px;
  border-radius: 6px;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  cursor: pointer;
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.color.background.tertiary : theme.color.background.primary};
  border: 0.5px solid
    ${({ theme, $isSelected }) =>
      $isSelected ? theme.color.border.selected : theme.color.border.primary};

  transition:
    background-color 0.075s ease-in-out,
    border-color 0.075s ease-in-out;

  &:hover {
    border-color: ${({ theme, $isSelected }) =>
      $isSelected ? theme.color.border.selected : theme.color.border.secondary};
    background-color: ${({ theme }) => theme.color.background.tertiary};
  }
`);

interface MappingMethodCardState {
  isHovered: boolean;
}

const DEFAULT_STATE: MappingMethodCardState = {
  isHovered: false,
};

const MappingMethodCard = ({ method, isSelected, onSelect }: MappingMethodCardProps) => {
  const [localState, setLocalState] = useState<MappingMethodCardState>(DEFAULT_STATE);

  const isRecommended = MAPPING_METHOD_TO_IS_RECOMMENDED_MAP[method];

  const handleMouseEnter = useCallback(() => {
    setLocalState((prev) => ({ ...prev, isHovered: true }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    setLocalState((prev) => ({ ...prev, isHovered: false }));
  }, []);

  const renderMappingMethodGraphic = useCallback(() => {
    return match(method)
      .returnType<React.ReactNode>()
      .with(ProjectMappingMethod.UNSPECIFIED, () => <></>)
      .with(ProjectMappingMethod.AUTO, () => <MappingMethodCardAutoIntrospectGraphic />)
      .with(ProjectMappingMethod.JSON, () => (
        <MappingMethodCardJsonGraphic isHovered={localState.isHovered || isSelected} />
      ))
      .with(ProjectMappingMethod.BLANK, () => (
        <MappingMethodCardBlankGraphic isHovered={localState.isHovered || isSelected} />
      ))
      .with(P.number, () => <></>)
      .exhaustive();
  }, [method, localState.isHovered, isSelected]);

  return (
    <MappingMethodCardWrapper
      $isSelected={isSelected}
      $isRecommended={isRecommended}
      onClick={onSelect}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {renderMappingMethodGraphic()}
      <FlexWrapper direction={FlexDirection.COLUMN} alignItems={AlignItems.CENTER} gap={8}>
        <FlexWrapper alignItems={AlignItems.CENTER} justifyContent={JustifyContent.CENTER} gap={8}>
          {MAPPING_METHOD_TO_ICON_MAP[method]}
          <Text size={TextSize.BODY_LG}>{MAPPING_METHOD_TO_LABEL_MAP[method]}</Text>
          {isRecommended && (
            <Text size={TextSize.BODY_SM} variant={TextVariant.TEAL}>
              Recommended
            </Text>
          )}
        </FlexWrapper>
        <Text variant={TextVariant.TERTIARY} align="center">
          {MAPPING_METHOD_TO_DESCRIPTION_MAP[method]}
        </Text>
      </FlexWrapper>
    </MappingMethodCardWrapper>
  );
};

export default MappingMethodCard;
